import * as React from 'react';
import { cn } from '../../lib/utils';
import { Button } from './button';
import { IconButton } from './icon-button';
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  MoreHorizontal,
} from 'lucide-react';

/**
 * Pagination component with Hyperkit styling
 *
 * Features:
 * - First, Previous, page numbers with ellipsis, Next, Last buttons
 * - Configurable siblings count for visible page numbers
 * - Dark mode support
 * - Follows Radix patterns with forwardRef
 */

/**
 * Generate page numbers array with ellipsis
 * @param {number} currentPage - Current active page
 * @param {number} totalPages - Total number of pages
 * @param {number} siblingsCount - Number of siblings on each side of current page
 * @returns {Array} Array of page numbers and ellipsis markers
 */
function generatePagination(currentPage, totalPages, siblingsCount = 1) {
  // Always show first page, last page, current page, and siblings
  const pages = [];

  // Calculate range around current page
  const leftSiblingIndex = Math.max(currentPage - siblingsCount, 1);
  const rightSiblingIndex = Math.min(currentPage + siblingsCount, totalPages);

  // Determine if we need ellipsis
  const showLeftEllipsis = leftSiblingIndex > 2;
  const showRightEllipsis = rightSiblingIndex < totalPages - 1;

  // Always include page 1
  pages.push(1);

  // Left ellipsis or page 2
  if (showLeftEllipsis) {
    pages.push('ellipsis-left');
  } else if (leftSiblingIndex > 1) {
    // Add pages between 1 and leftSiblingIndex
    for (let i = 2; i < leftSiblingIndex; i++) {
      pages.push(i);
    }
  }

  // Add sibling pages and current page
  for (let i = leftSiblingIndex; i <= rightSiblingIndex; i++) {
    if (i !== 1 && i !== totalPages) {
      pages.push(i);
    }
  }

  // Right ellipsis or remaining pages
  if (showRightEllipsis) {
    pages.push('ellipsis-right');
  } else if (rightSiblingIndex < totalPages) {
    // Add pages between rightSiblingIndex and totalPages
    for (let i = rightSiblingIndex + 1; i < totalPages; i++) {
      pages.push(i);
    }
  }

  // Always include last page if more than 1 page
  if (totalPages > 1) {
    pages.push(totalPages);
  }

  return pages;
}

const PaginationEllipsis = React.forwardRef(({ className, ...props }, ref) => (
  <span
    ref={ref}
    aria-hidden
    className={cn(
      'flex h-8 w-8 items-center justify-center text-grey-500 dark:text-grey-400',
      className
    )}
    {...props}
  >
    <MoreHorizontal className="h-4 w-4" />
    <span className="sr-only">More pages</span>
  </span>
));
PaginationEllipsis.displayName = 'PaginationEllipsis';

const Pagination = React.forwardRef(
  (
    {
      currentPage,
      totalPages,
      onPageChange,
      siblingsCount = 1,
      className,
      showFirstLast = true,
      ...props
    },
    ref
  ) => {
    // Don't render if only 1 page or less
    if (totalPages <= 1) {
      return null;
    }

    const pages = generatePagination(currentPage, totalPages, siblingsCount);

    const isFirstPage = currentPage === 1;
    const isLastPage = currentPage === totalPages;

    return (
      <nav
        ref={ref}
        role="navigation"
        aria-label="Pagination"
        className={cn('flex items-center justify-center gap-1', className)}
        {...props}
      >
        {/* First page button */}
        {showFirstLast && (
          <IconButton
            variant="plain"
            size="md"
            onClick={() => onPageChange(1)}
            disabled={isFirstPage}
            aria-label="Go to first page"
          >
            <ChevronsLeft className="h-4 w-4" />
          </IconButton>
        )}

        {/* Previous page button */}
        <IconButton
          variant="plain"
          size="md"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={isFirstPage}
          aria-label="Go to previous page"
        >
          <ChevronLeft className="h-4 w-4" />
        </IconButton>

        {/* Page numbers */}
        <div className="flex items-center gap-1">
          {pages.map((page, index) => {
            if (page === 'ellipsis-left' || page === 'ellipsis-right') {
              return <PaginationEllipsis key={page} />;
            }

            const isCurrentPage = page === currentPage;

            return (
              <Button
                key={page}
                variant={isCurrentPage ? 'default' : 'ghost'}
                size="icon"
                onClick={() => onPageChange(page)}
                aria-label={`Go to page ${page}`}
                aria-current={isCurrentPage ? 'page' : undefined}
                className={cn(
                  'h-8 w-8 rounded-lg text-sm font-medium',
                  isCurrentPage
                    ? 'bg-grey-950 text-white dark:bg-grey-50 dark:text-grey-950'
                    : 'text-grey-700 hover:bg-grey-100 dark:text-grey-300 dark:hover:bg-grey-800'
                )}
              >
                {page}
              </Button>
            );
          })}
        </div>

        {/* Next page button */}
        <IconButton
          variant="plain"
          size="md"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={isLastPage}
          aria-label="Go to next page"
        >
          <ChevronRight className="h-4 w-4" />
        </IconButton>

        {/* Last page button */}
        {showFirstLast && (
          <IconButton
            variant="plain"
            size="md"
            onClick={() => onPageChange(totalPages)}
            disabled={isLastPage}
            aria-label="Go to last page"
          >
            <ChevronsRight className="h-4 w-4" />
          </IconButton>
        )}
      </nav>
    );
  }
);
Pagination.displayName = 'Pagination';

export { Pagination, PaginationEllipsis, generatePagination };
